import { useMemo } from 'react';

import type { ClaimNotificationModel } from '~/components/ClaimNotificationsCard/types';
import useIdBasedPagination, { type UseIdBasedPaginationReturn } from '~/hooks/useIdBasedPagination';
import type { PageDataResponseDictNotificationsPageMetaNotificationsPageMeta as NotificationsPageMeta } from '~/server_shared/openapi/data-contracts';

export interface NotificationsPaginationParams {
  should_return_dismissed?: boolean;
  should_return_future_reminders?: boolean;
  sortByColumn?: string;
  withReadStatus?: boolean;
  typeSubtypeFilters?: Array<{ type: string; subtype: string | null }>;
  subOrganizationIds?: (number | string)[];
  categoriesIds?: (number | string)[];
  statuses?: string[];
  priorities?: string[];
  startDueDateTime?: string | null;
  endDueDateTime?: string | null;
  adjustersIds?: (number | string)[];
  claimId?: number;
  organizationId?: number;
}

export const useNotificationsPagination = (
  params: NotificationsPaginationParams,
  initialRowsPerPage?: number
): UseIdBasedPaginationReturn<ClaimNotificationModel, null, NotificationsPageMeta> => {
  const fetchIdsUrl = '/api/v1/claims/claims_notifications_ids';
  const fetchPageDataUrl = '/api/v1/claims/claims_notifications_by_ids';

  // Transform camelCase frontend params to snake_case backend params
  const fetchIdsParams = useMemo(() => {
    const transformedParams = {
      organization_id: params.organizationId,
      show_future_reminders: params.should_return_future_reminders,
      include_dismissed: params.should_return_dismissed ? 'only_dismissed' : 'not_dismissed',
      claim_id: params.claimId,
      sort_by_column: params.sortByColumn,
      with_read_status: params.withReadStatus,
      type_subtype_filters: params.typeSubtypeFilters?.map((filter) =>
        filter.subtype ? `${filter.type}.${filter.subtype}` : filter.type
      ),
      sub_organization_ids: params.subOrganizationIds,
      categories_ids: params.categoriesIds,
      statuses: params.statuses,
      priorities: params.priorities,
      start_due_datetime: params.startDueDateTime,
      end_due_datetime: params.endDueDateTime,
      adjusters_ids: params.adjustersIds?.map((id) => String(id)),
    };

    return transformedParams;
  }, [
    params.organizationId,
    params.should_return_future_reminders,
    params.should_return_dismissed,
    params.claimId,
    params.sortByColumn,
    params.withReadStatus,
    params.typeSubtypeFilters,
    params.subOrganizationIds,
    params.categoriesIds,
    params.statuses,
    params.priorities,
    params.startDueDateTime,
    params.endDueDateTime,
    params.adjustersIds,
  ]);

  const fetchPageDataParams = useMemo(() => {
    const transformedParams = {
      claim_id: params.claimId,
      organization_id: params.organizationId,
    };

    return transformedParams;
  }, [params.claimId, params.organizationId]);

  const result = useIdBasedPagination<
    number, // IdType
    null, // ExtraDataType
    ClaimNotificationModel, // PageResultType
    NotificationsPageMeta // PageMetaDataType
  >({
    fetchIdsUrl,
    fetchPageDataUrl,
    fetchIdsParams,
    fetchPageDataParams,
    pageDataIdKey: 'notification_ids',
    initialRowsPerPage,
  });

  return result;
};
